import React from 'react'
import { useConversations } from '@/hooks/useConversations'
import { formatDistanceToNow } from 'date-fns'
import { MessageCircle, Loader2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'

interface ConversationsListProps {
  onSelectConversation: (conversationId: string, otherUser: any) => void
}

export function ConversationsList({ onSelectConversation }: ConversationsListProps) {
  const { user } = useAuth()
  const { conversations, loading } = useConversations()

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    )
  }

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <MessageCircle className="h-12 w-12 text-gray-400 mb-4" />
        <p className="text-gray-600">No conversations yet</p>
        <p className="text-sm text-gray-500 mt-1">
          Start chatting with vendors or buyers
        </p>
      </div>
    )
  }

  return (
    <div className="divide-y divide-gray-200">
      {conversations.map((conversation) => {
        // Determine other user
        const otherUser =
          conversation.participant_1_id === user?.id
            ? conversation.participant_2
            : conversation.participant_1

        const displayName = otherUser.display_name || otherUser.full_name || 'Anonymous'

        return (
          <button
            key={conversation.id}
            onClick={() => onSelectConversation(conversation.id, otherUser)}
            className="w-full flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors text-left"
          >
            {otherUser.avatar_url ? (
              <img
                src={otherUser.avatar_url}
                alt={displayName}
                className="w-12 h-12 rounded-full flex-shrink-0"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-medium text-gray-600">
                  {displayName[0]}
                </span>
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="font-semibold truncate">
                  {displayName}
                </h3>
                {conversation.last_message_at && (
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    {formatDistanceToNow(new Date(conversation.last_message_at), {
                      addSuffix: true
                    })}
                  </span>
                )}
              </div>
              {conversation.last_message_preview && (
                <p className="text-sm text-gray-600 truncate mt-1">
                  {conversation.last_message_preview}
                </p>
              )}
            </div>
          </button>
        )
      })}
    </div>
  )
}
